/**
 * BrandVoicePanel — simplified "Reglas del copy" editor.
 *
 * A single free-form textarea where the user writes every rule the copy
 * must follow, one per line. There used to be a do/dont split and an
 * approved-examples CRUD; both were removed per product spec: the
 * prompt that goes to the AI = these rules + the user's chat message.
 * Nothing else.
 *
 * The save action persists the blob to `/brand-voice` via PUT and the
 * Copy Studio prompt builder reads it from there.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useBrand } from "../lib/brand";
import { Save } from "lucide-react";

const PLACEHOLDER = `Una regla por línea. Por ejemplo:

- Siempre escribe "Nuam" con la N en mayúscula.
- Nunca uses hashtags.
- Tono cercano, hablamos de tú.
- Máximo 4 líneas, separadas por línea en blanco.
- Termina siempre con una llamada a la acción clara.
- Evita las palabras "barato" y "gratis".
- Usa emojis con moderación, máximo 2 por copy.`;

export default function BrandVoicePanel({ onChanged }) {
    const { currentBrand } = useBrand();
    const [rules, setRules] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!currentBrand?.id) return;
        setLoading(true);
        api.get("/brand-voice")
            .then((r) => setRules(r.data?.rules || ""))
            .catch(() => toast.error("No se pudieron cargar las reglas"))
            .finally(() => setLoading(false));
    }, [currentBrand?.id]);

    const save = async () => {
        setSaving(true);
        try {
            const r = await api.put("/brand-voice", { rules });
            setRules(r.data?.rules || "");
            toast.success("Reglas guardadas");
            // Surface a coarse counter so the Copy Studio status bar can
            // show "X reglas activas" if it wants — best-effort.
            const count = (r.data?.rules || "")
                .split(/\r?\n/)
                .filter((l) => l.trim()).length;
            onChanged?.({ enabledCount: count, hasRules: !!(r.data?.rules || "").trim() });
        } catch {
            toast.error("No se pudo guardar");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="text-sm text-fog-500 font-mono p-6">Cargando…</div>;
    }

    const lineCount = rules.split(/\r?\n/).filter((l) => l.trim()).length;

    return (
        <div className="space-y-4" data-testid="brand-voice-panel">
            <section className="rounded-2xl border hairline border-ink-600 bg-ink-900/40 p-5 space-y-4">
                <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                        <h2 className="font-mono text-[10px] uppercase tracking-[0.25em] text-fog-400">
                            Reglas del copy
                        </h2>
                        <p className="text-xs text-fog-400 mt-2 max-w-xl">
                            Escribe en bullets todo lo que el copy generado debe
                            cumplir: tono, longitud, palabras prohibidas,
                            casing, estructura, plataforma, etc. Todo lo que
                            pongas aquí va al prompt; nada más se le envía
                            al modelo aparte de tu mensaje del chat.
                        </p>
                    </div>
                    <div className="flex flex-col items-end gap-2 shrink-0">
                        <button
                            data-testid="voice-save-btn"
                            onClick={save}
                            disabled={saving}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-periwinkle-500 hover:bg-periwinkle-400 text-ink-950 text-sm font-medium disabled:opacity-40 shadow-sm"
                        >
                            <Save className="w-4 h-4" />
                            {saving ? "Guardando…" : "Guardar reglas"}
                        </button>
                        <span className="font-mono text-[10px] uppercase tracking-widest text-fog-500">
                            {lineCount} {lineCount === 1 ? "regla" : "reglas"}
                        </span>
                    </div>
                </div>
                <textarea
                    data-testid="voice-rules-textarea"
                    value={rules}
                    onChange={(e) => setRules(e.target.value)}
                    rows={18}
                    placeholder={PLACEHOLDER}
                    className="w-full px-4 py-3 rounded-xl border hairline border-ink-600 bg-ink-950 text-fog-100 text-sm leading-relaxed focus:border-periwinkle-500/50 focus:outline-none resize-y font-mono placeholder:text-fog-600"
                />
                <p className="font-mono text-[10px] uppercase tracking-widest text-fog-500">
                    Una regla por línea
                </p>
            </section>
        </div>
    );
}
